import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import useTable from "../../../../shared/table/useTable";
import {
  Avatar,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  MenuItem,
  Menu,
  TableContainer,
  TableHead,
  TablePagination,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import { useTranslation } from "react-i18next";
import DrawerEditManager from "./DrawerEditManager";

const StyledCellHeader = styled(TableCell)({
  borderBottom: "none",
  color: "#637381",
  backgroundColor: "#f4f6f8",
  "&:first-of-type": {
    paddingLeft: 24,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  "&:last-of-type": {
    paddingRight: 24,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
});

const StyledRowContent = styled(TableRow)({
  cursor: "pointer",
  textDecoration: "none",
  "&.MuiTableRow-hover:hover": {
    backgroundColor: "transparent",
    "& .sticky": {
      backgroundColor: "#FFFFFF",
    },
  },
});

const StyledCellContent = styled(TableCell)({
  borderBottom: "1px dashed rgba(224, 224, 224, 1)",
  padding: "8px 16px",
  "&.sticky": {
    position: "sticky",
    left: 0,
    backgroundColor: "#FFFFFF",
    boxShadow: "8px 20px 20px #EEEEEE",
  },
});

const StyledMenu = styled(Menu)({
  "& .MuiMenu-paper": {
    borderRadius: 8,
    boxShadow:
      "rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 12px 24px 0px",
  },
  "& .MuiList-root": {
    padding: 8,
  },
  "& .MuiMenuItem-root": {
    paddingLeft: 8,
    paddingRight: 8,
    borderRadius: 6,
    "&:hover": {
      textDecoration: "none",
      backgroundColor: "#919eab14",
    },
  },
});

function DataTable(props) {
  const { t, i18n } = useTranslation();
  const { managerList, company, handleChangeManagerEdit } = props;

  const open = Boolean(anchorEl);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const [drawerManagerConfig, setDrawerManagerConfig] = useState({
    open: false,
    isEdit: false,
    values: {
      idManagerEmployee: null,
      name: null,
      positionsName: null,
    },
  });

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <div>
      <TableContainer height={250}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <StyledCellHeader className="sticky" />
              <StyledCellHeader className="sticky">
                {t("FullName")}
              </StyledCellHeader>
              <StyledCellHeader className="sticky">
                {t("PositionName")}
              </StyledCellHeader>
              <StyledCellHeader className="sticky">
                {t("Email")}
              </StyledCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {managerList ? (
              (managerList.length > 0 &&
                managerList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <StyledRowContent key={item.idManagerEmployee}>
                      <StyledCellContent>
                        <IconButton
                          aria-controls={open ? "more-menu" : undefined}
                          aria-label="more"
                          size="small"
                          onClick={() => {
                            setDrawerManagerConfig((prev) => ({
                              ...prev,
                              open: true,
                              isEdit: true,
                              values: {
                                idManagerEmployee: item.idManagerEmployee,
                                name: item.name,
                                positionsName: item.positionsName,
                                idUsers: item.idUsers,
                              },
                            }));
                          }}
                        >
                          <EditRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </StyledCellContent>
                      <StyledCellContent>
                        <Typography>{item.name}</Typography>
                      </StyledCellContent>
                      <StyledCellContent>
                        <Typography>{item.positionsName}</Typography>
                      </StyledCellContent>
                      <StyledCellContent>
                        <Typography>{item.email}</Typography>
                      </StyledCellContent>
                    </StyledRowContent>
                  ))) || (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No Data
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {managerList && managerList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5]}
            component="div"
            count={managerList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        )}
      </TableContainer>

      <StyledMenu
        keepMounted
        id={`more-menu`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCloseMenu}>Reset password</MenuItem>
      </StyledMenu>

      <DrawerEditManager
        open={drawerManagerConfig.open}
        onClose={() => {
          setDrawerManagerConfig((prev) => ({ ...prev, open: false }));
        }}
        isEdit={drawerManagerConfig.isEdit}
        values={drawerManagerConfig.values}
        selectedCompany={company}
      />
    </div>
  );
}

export default DataTable;
